// =============================================================================
// Fase 5.5 — Step 3: Documenti di convenzione (dinamico per soggetto/tipo)
// Salta se tipo_richiesta = "contatto"
// =============================================================================
import React from "react";
import { Info } from "lucide-react";
import { DocUploader } from "./DocUploader";
import { DOC_IDS } from "../../hooks/useRcaWizard";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const PARENTELA_OPTS = [
  ["coniuge", "Coniuge"], ["padre", "Padre"], ["madre", "Madre"],
  ["figlio", "Figlio"], ["figlia", "Figlia"],
  ["fratello", "Fratello"], ["sorella", "Sorella"], ["altro", "Altro convivente"],
];

export function Step3Documenti({ state, setField, docs, requiredDocs, setDoc, removeDoc }) {
  const { soggetto_richiesta: soggetto, tipo_richiesta: tipo } = state;
  const reqSet = new Set(requiredDocs.required);
  const optSet = new Set(requiredDocs.optional);

  const docMeta = {
    [DOC_IDS.DOC_ID_DIP]: {
      label: "Documento d'identità del dipendente",
      desc: "Carta d'identità o patente in corso di validità (fronte/retro).",
    },
    [DOC_IDS.BADGE_DIP]: {
      label: "Badge aziendale del dipendente",
      desc: "Foto o scansione del badge attivo.",
    },
    [DOC_IDS.BUSTA_DIP]: {
      label: "Ultima busta paga del dipendente",
      desc: "Utile per la verifica della convenzione attiva.",
    },
    [DOC_IDS.STATO_FAM]: {
      label: "Stato di famiglia",
      desc: "Certificato del Comune o autocertificazione — dimostra la convivenza col dipendente titolare.",
    },
    [DOC_IDS.DOC_ID_FAM]: {
      label: "Documento d'identità del familiare (assicurato)",
      desc: "Carta d'identità o patente del familiare che sarà contraente.",
    },
    [DOC_IDS.LIB_VEIC]: {
      label: "Libretto del veicolo da assicurare",
      desc: "Foto o scansione del libretto (carta di circolazione).",
    },
    [DOC_IDS.ATTESTATO]: {
      label: "Attestato di rischio (se disponibile)",
      desc: "Se non ce l'hai, puoi comunque inviare la richiesta.",
    },
  };

  const docsToShow = [...requiredDocs.required, ...requiredDocs.optional];

  return (
    <div className="space-y-4" data-testid="rca-step3">
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-[#0F1F14]">
          Documenti per la convenzione
        </h2>
        <p className="text-sm text-[#4A5E51] mt-1">
          {tipo === "rinnovo"
            ? "Per il rinnovo è sufficiente allegare il libretto del veicolo. Un nostro esperto verificherà la soluzione più adatta alla tua convenzione."
            : "Carica i documenti richiesti in base alla tua situazione. Ti chiediamo solo il minimo necessario."}
        </p>
      </div>

      {/* Se familiare: chiedi dati del dipendente titolare (CF serve subito per matching) */}
      {soggetto === "familiare" && (
        <div
          className="rounded-2xl border border-[#E5E2DB] bg-[#FAF9F5] p-4 space-y-3"
          data-testid="rca-dipendente-titolare-block"
        >
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-[#4A5E51] mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-[#4A5E51]">
              Dati del <strong>dipendente titolare</strong> della convenzione (a cui sei legato dallo stato di famiglia).
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nome titolare *</Label>
              <Input
                value={state.dipendente_titolare.nome}
                onChange={(e) => setField("dipendente_titolare.nome", e.target.value)}
                data-testid="rca-titolare-nome"
              />
            </div>
            <div>
              <Label className="text-xs">Cognome titolare *</Label>
              <Input
                value={state.dipendente_titolare.cognome}
                onChange={(e) => setField("dipendente_titolare.cognome", e.target.value)}
                data-testid="rca-titolare-cognome"
              />
            </div>
            <div>
              <Label className="text-xs">Codice fiscale titolare *</Label>
              <Input
                value={state.dipendente_titolare.codice_fiscale}
                onChange={(e) =>
                  setField(
                    "dipendente_titolare.codice_fiscale",
                    e.target.value.toUpperCase().replace(/\s/g, "")
                  )
                }
                maxLength={16}
                data-testid="rca-titolare-cf"
                placeholder="RSSMRA80A01H501Z"
              />
            </div>
            <div>
              <Label className="text-xs">Parentela *</Label>
              <Select
                value={state.dipendente_titolare.parentela}
                onValueChange={(v) => setField("dipendente_titolare.parentela", v)}
              >
                <SelectTrigger className="h-10 text-sm" data-testid="rca-titolare-parentela">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent>
                  {PARENTELA_OPTS.map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {docsToShow.map((docId) => (
          <DocUploader
            key={docId}
            docId={docId}
            label={docMeta[docId]?.label || docId}
            description={docMeta[docId]?.desc}
            required={reqSet.has(docId)}
            optional={optSet.has(docId)}
            currentDoc={docs[docId]}
            onUploaded={(id, p) => setDoc(id, { ...p, status: "caricato" })}
            onCleared={(id) => removeDoc(id, false)}
            testid={`rca-doc-${docId}`}
          />
        ))}
      </div>
    </div>
  );
}
